/**
 * Registry of named avatar animation states.
 *
 * Each entry is a composition of:
 *   * ``base``     — which of the 8 source SVGs to render underneath.
 *   * ``motion``   — an additive framer-motion behavior applied to the
 *                    avatar wrapper (bounce / wiggle / spin / etc.).
 *   * ``overlay``  — a small emoji / glyph badge floating over the
 *                    avatar (hearts / stars / zzz / fire / etc.).
 *
 * The registry powers the mood engine: ``chooseAnimation(mood, base)``
 * picks a stable animation for each (mood, base) pair so the avatar
 * feels intentional instead of random. The 60-second base shuffle in
 * ``useShuffledAvatars`` swaps the CHARACTER; the mood engine picks
 * the ANIMATION on top of it.
 *
 * Total entries: 56 (well over the ≥50 target). See the trailing
 * comment for the full audit-friendly listing.
 */

// Base identifiers (must match the imports in ../lib/avatars.js).
export const BASES = [
  "idle-blink",
  "sleep",
  "charging",
  "full-happy",
  "pat-happy",
  "dizzy",
  "swing",
  "overheat",
];

// Motion presets — pure transform/opacity so 60fps is trivial. Each
// preset returns a framer ``animate`` object suitable for a wrapping
// ``motion.div``. All loops are infinite unless caller keys the
// element with a transient reaction — see ChargerIcon.
export const MOTIONS = {
  none: { animate: { y: 0 }, transition: { duration: 0 } },
  breathe: {
    animate: { scale: [1, 1.05, 1] },
    transition: { duration: 2.6, ease: "easeInOut", repeat: Infinity },
  },
  bounce: {
    animate: { y: [0, -4, 0] },
    transition: { duration: 1.1, ease: "easeInOut", repeat: Infinity },
  },
  jump: {
    animate: { y: [0, -9, 0] },
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], repeat: Infinity },
  },
  wiggle: {
    animate: { rotate: [-4, 4, -4] },
    transition: { duration: 1.4, ease: "easeInOut", repeat: Infinity },
  },
  shake: {
    animate: { x: [-2, 2, -2, 2, 0] },
    transition: { duration: 0.5, ease: "easeInOut", repeat: Infinity },
  },
  spin: {
    animate: { rotate: [0, 360] },
    transition: { duration: 2.2, ease: "linear", repeat: Infinity },
  },
  "tilt-run": {
    animate: { rotate: [-3, 3], x: [-1, 1] },
    transition: { duration: 0.4, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" },
  },
  pulse: {
    animate: { scale: [1, 1.14, 1] },
    transition: { duration: 0.9, ease: "easeInOut", repeat: Infinity },
  },
  party: {
    animate: { y: [0, -6, 0], rotate: [-8, 8, -8] },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], repeat: Infinity },
  },
  // One-shot reaction motions. Used by ChargerIcon when
  // ``reactionKey`` bumps — animate once and settle.
  "one-jump": {
    animate: { y: [0, -14, 0], scale: [1, 1.14, 1] },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
  "one-spin": {
    animate: { rotate: [0, 360], scale: [1, 1.12, 1] },
    transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1] },
  },
  "one-dizzy": {
    animate: { rotate: [0, -14, 14, -8, 0], x: [0, -2, 2, -1, 0] },
    transition: { duration: 0.65, ease: "easeInOut" },
  },
};

// Overlay presets. Rendered as an absolutely-positioned tiny badge in
// the corner of the avatar frame. `char` may be a lucide-style glyph
// or a unicode symbol — kept ASCII / simple where possible so we don't
// depend on emoji-font availability.
export const OVERLAYS = {
  none: null,
  hearts: { char: "♥", color: "#ff6b8a", corner: "tr" },
  stars: { char: "★", color: "#ffd166", corner: "tr" },
  zzz: { char: "z", color: "#8fb2ff", corner: "tr" },
  fire: { char: "▲", color: "#ff8a5c", corner: "tr" }, // triangle stand-in for flame
  sweat: { char: "◒", color: "#7fd3ff", corner: "tl" },
  tears: { char: "•", color: "#7fd3ff", corner: "bl" },
  halo: { char: "◜", color: "#ffd166", corner: "tc" },
  lightning: { char: "⚡", color: "rgb(var(--accent))", corner: "tr" },
  sparkle: { char: "✧", color: "rgb(var(--accent))", corner: "tr" },
  bubble: { char: "◌", color: "#8fb2ff", corner: "tl" },
};

// The full registry — 56 named combinations.
export const ANIMATIONS = [
  // idle-blink family (7)
  { name: "idle-still",           base: "idle-blink", motion: "none",     overlay: "none" },
  { name: "idle-breathe",         base: "idle-blink", motion: "breathe",  overlay: "none" },
  { name: "idle-halo",            base: "idle-blink", motion: "breathe",  overlay: "halo" },
  { name: "idle-sparkle",         base: "idle-blink", motion: "wiggle",   overlay: "sparkle" },
  { name: "idle-hearts",          base: "idle-blink", motion: "breathe",  overlay: "hearts" },
  { name: "idle-tilt",            base: "idle-blink", motion: "tilt-run", overlay: "none" },
  { name: "idle-pulse-star",      base: "idle-blink", motion: "pulse",    overlay: "stars" },

  // sleep family (5)
  { name: "sleep-zzz",            base: "sleep",      motion: "breathe",  overlay: "zzz" },
  { name: "sleep-bubble",         base: "sleep",      motion: "breathe",  overlay: "bubble" },
  { name: "sleep-quiet",          base: "sleep",      motion: "none",     overlay: "none" },
  { name: "sleep-drift",          base: "sleep",      motion: "wiggle",   overlay: "zzz" },
  { name: "sleep-halo",           base: "sleep",      motion: "breathe",  overlay: "halo" },

  // charging family (7)
  { name: "charging-pulse",       base: "charging",   motion: "pulse",    overlay: "lightning" },
  { name: "charging-bounce",      base: "charging",   motion: "bounce",   overlay: "lightning" },
  { name: "charging-spark",       base: "charging",   motion: "wiggle",   overlay: "sparkle" },
  { name: "charging-jump",        base: "charging",   motion: "jump",     overlay: "lightning" },
  { name: "charging-tilt",        base: "charging",   motion: "tilt-run", overlay: "lightning" },
  { name: "charging-focus",       base: "charging",   motion: "breathe",  overlay: "none" },
  { name: "charging-stars",       base: "charging",   motion: "pulse",    overlay: "stars" },

  // full-happy family (8) — used for target-met / winner celebrations
  { name: "full-party",           base: "full-happy", motion: "party",    overlay: "stars" },
  { name: "full-jump-hearts",     base: "full-happy", motion: "jump",     overlay: "hearts" },
  { name: "full-spin",            base: "full-happy", motion: "spin",     overlay: "stars" },
  { name: "full-bounce-hearts",   base: "full-happy", motion: "bounce",   overlay: "hearts" },
  { name: "full-sparkle-halo",    base: "full-happy", motion: "breathe",  overlay: "halo" },
  { name: "full-pulse-star",      base: "full-happy", motion: "pulse",    overlay: "stars" },
  { name: "full-wiggle-hearts",   base: "full-happy", motion: "wiggle",   overlay: "hearts" },
  { name: "full-glow",            base: "full-happy", motion: "breathe",  overlay: "sparkle" },

  // pat-happy family (6)
  { name: "pat-jump",             base: "pat-happy",  motion: "jump",     overlay: "hearts" },
  { name: "pat-bounce",           base: "pat-happy",  motion: "bounce",   overlay: "hearts" },
  { name: "pat-wiggle",           base: "pat-happy",  motion: "wiggle",   overlay: "sparkle" },
  { name: "pat-party",            base: "pat-happy",  motion: "party",    overlay: "stars" },
  { name: "pat-pulse",            base: "pat-happy",  motion: "pulse",    overlay: "hearts" },
  { name: "pat-breathe",          base: "pat-happy",  motion: "breathe",  overlay: "hearts" },

  // dizzy family (6)
  { name: "dizzy-spin",           base: "dizzy",      motion: "spin",     overlay: "none" },
  { name: "dizzy-shake",          base: "dizzy",      motion: "shake",    overlay: "sweat" },
  { name: "dizzy-tears",          base: "dizzy",      motion: "wiggle",   overlay: "tears" },
  { name: "dizzy-slow",           base: "dizzy",      motion: "wiggle",   overlay: "bubble" },
  { name: "dizzy-drop",           base: "dizzy",      motion: "shake",    overlay: "tears" },
  { name: "dizzy-still",          base: "dizzy",      motion: "none",     overlay: "sweat" },

  // swing family (7)
  { name: "swing-run",            base: "swing",      motion: "tilt-run", overlay: "sparkle" },
  { name: "swing-jump",           base: "swing",      motion: "jump",     overlay: "stars" },
  { name: "swing-hype",           base: "swing",      motion: "bounce",   overlay: "lightning" },
  { name: "swing-power",          base: "swing",      motion: "pulse",    overlay: "lightning" },
  { name: "swing-party",          base: "swing",      motion: "party",    overlay: "stars" },
  { name: "swing-shake",          base: "swing",      motion: "shake",    overlay: "none" },
  { name: "swing-focus",          base: "swing",      motion: "breathe",  overlay: "none" },

  // overheat family (7)
  { name: "overheat-fire",        base: "overheat",   motion: "shake",    overlay: "fire" },
  { name: "overheat-sweat",       base: "overheat",   motion: "wiggle",   overlay: "sweat" },
  { name: "overheat-panic",       base: "overheat",   motion: "shake",    overlay: "sweat" },
  { name: "overheat-blaze",       base: "overheat",   motion: "pulse",    overlay: "fire" },
  { name: "overheat-jump-fire",   base: "overheat",   motion: "jump",     overlay: "fire" },
  { name: "overheat-tilt",        base: "overheat",   motion: "tilt-run", overlay: "fire" },
  { name: "overheat-still",       base: "overheat",   motion: "breathe",  overlay: "fire" },

  // reaction-only entries (3) — used when a card explicitly reacts to
  // ±1 or a target-met, then settles back to mood.
  { name: "reaction-plus",        base: "pat-happy",  motion: "one-jump", overlay: "sparkle" },
  { name: "reaction-target",      base: "full-happy", motion: "one-spin", overlay: "stars" },
  { name: "reaction-minus",       base: "dizzy",      motion: "one-dizzy", overlay: "tears" },
];

/**
 * Choose an animation for the given (mood, baseIndex, reaction).
 *
 *   * When ``reaction`` is truthy we always return the matching
 *     one-shot entry — it visually overrides the mood for ~1.5 s.
 *   * Otherwise we filter the registry to entries plausible for the
 *     mood and pick one deterministically from ``baseIndex`` so the
 *     minute-shuffle changes CHARACTER without shuffling MOOD.
 *
 * mood ∈ {"sleep","charging","neutral","winner","full","overflow","loser"}
 */
export function chooseAnimation(mood, baseIndex = 0, reaction = null) {
  if (reaction) {
    // Six-tier reaction ladder (Feb-2026). Each tier maps to a
    // specific registry entry with escalating intensity — motion
    // gets bolder and overlays get louder as the count climbs.
    // plus4 is the "screaming" (chillano) tier the user asked for.
    const map = {
      plus1:  "idle-hearts",           // gentle first-click smile
      plus2:  "pat-jump",              // clap / applause energy
      plus3:  "swing-hype",            // excited jump / fist pump
      plus4:  "overheat-jump-fire",    // SCREAMING celebration
      plus5:  "overheat-blaze",        // mind blown / evil cackle vibe
      plus6:  "swing-power",           // maximum hype
      plus:   "reaction-plus",         // legacy fallback
      target: "reaction-target",
      minus:  "reaction-minus",
    };
    return ANIMATIONS.find((a) => a.name === map[reaction]) || ANIMATIONS[0];
  }
  const buckets = {
    sleep:    ["sleep-zzz","sleep-bubble","sleep-quiet","sleep-drift","sleep-halo"],
    charging: ["charging-pulse","charging-bounce","charging-spark","charging-jump","charging-tilt","charging-focus","charging-stars"],
    neutral:  ["idle-still","idle-breathe","idle-halo","idle-tilt","idle-pulse-star"],
    winner:   ["full-party","full-jump-hearts","full-spin","pat-jump","pat-party","swing-party","swing-hype"],
    full:     ["full-party","full-bounce-hearts","full-sparkle-halo","full-wiggle-hearts","full-glow","pat-bounce","pat-pulse"],
    overflow: ["overheat-fire","overheat-blaze","overheat-jump-fire","overheat-tilt","swing-power","swing-shake"],
    // Big overshoot — maximum celebration: spinning, star-studded,
    // partying. Fires on the dedicated `starstruck` mood tier.
    starstruck: ["full-spin","full-party","full-pulse-star","swing-party","overheat-blaze","overheat-jump-fire","swing-power"],
    loser:    ["dizzy-shake","dizzy-tears","dizzy-drop","dizzy-slow","overheat-sweat","overheat-panic"],
  };
  const pool = buckets[mood] || buckets.neutral;
  const name = pool[baseIndex % pool.length];
  return ANIMATIONS.find((a) => a.name === name) || ANIMATIONS[0];
}

// --------------------------------------------------------------------
// Audit list — total entries by family:
//   idle-blink : 7
//   sleep      : 5
//   charging   : 7
//   full-happy : 8
//   pat-happy  : 6
//   dizzy      : 6
//   swing      : 7
//   overheat   : 7
//   reactions  : 3
//   ---------- : --
//   TOTAL      : 56  (spec: ≥ 50)
// --------------------------------------------------------------------
